import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/axios";
import StickyTableContainer from "../ui/StickyTableContainer";

const today = new Date().toISOString().split("T")[0];
const numericCell = "border border-slate-300 px-3 py-2 text-center tabular-nums text-slate-800";
const textCell = "border border-slate-300 px-3 py-2 text-left text-slate-800";
const headerCell = "border border-slate-300 bg-slate-100 px-3 py-3 text-center text-xs font-bold text-slate-800";

const sum = (rows, field) => rows.reduce((total, row) => total + (Number(row[field]) || 0), 0);
const calculateTotals = (rows) => ({
  real_direct: sum(rows, "real_direct"),
  planned_direct: sum(rows, "planned_direct"),
  direct_gap: sum(rows, "direct_gap"),
  // Indirect staffing is a workshop-wide value repeated on every chain row.
  // Count it once in the footer instead of multiplying it by the chain count.
  real_indirect: Number(rows[0]?.real_indirect) || 0,
  planned_indirect: Number(rows[0]?.planned_indirect) || 0,
  indirect_gap: Number(rows[0]?.indirect_gap) || 0,
  real_production: sum(rows, "real_production"),
  planned_production: sum(rows, "planned_production"),
  daily_production_gap: sum(rows, "daily_production_gap"),
  cumulative_real_production: sum(rows, "cumulative_real_production"),
  cumulative_planned_production: sum(rows, "cumulative_planned_production"),
  cumulative_production_gap: sum(rows, "cumulative_production_gap"),
  real_worked_hours: sum(rows, "real_worked_hours"),
  planned_worked_hours: sum(rows, "planned_worked_hours"),
});
const displayNumber = (value) => Number.isInteger(Number(value))
  ? Number(value)
  : Number(value || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });
const displayMetric = (field, value) => field === "real_worked_hours" ? "" : displayNumber(value);
const isWorkforceGapAlert = (field, value) => ["direct_gap", "indirect_gap"].includes(field) && Number(value) > 0;
const isProductionGapAlert = (field, value) => field.includes("gap") && Number(value) < 0;

const DateControl = ({ date, onChange }) => (
  <div className="flex min-w-0 items-center gap-3">
    <label className="text-sm font-bold text-slate-800">Date</label>
    <input
      type="date"
      value={date}
      onChange={(event) => onChange(event.target.value)}
      className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 sm:flex-none"
    />
  </div>
);

const MobileRecap = ({ rows, metrics, totals, isGapAlert }) => (
  <div className="w-full max-w-full overflow-hidden rounded border border-slate-300 md:hidden">
    <table className="mobile-production-table w-full table-fixed border-collapse bg-white text-[6px] leading-[8px] text-slate-800">
      <colgroup>
        <col className="w-[9%]" />
        <col className="w-[9%]" />
        <col className="w-[10%]" />
        <col className="w-[9%]" />
        {metrics.map(([field]) => <col key={field} className="w-[7.875%]" />)}
      </colgroup>
      <thead className="bg-slate-100 font-semibold text-slate-700">
        <tr>
          <th title="Chaîne">Chaîne</th>
          <th title="Client">Client</th>
          <th title="Modèle">Modèle</th>
          <th title="Catégorie">Cat.</th>
          {metrics.map(([field, label, shortLabel]) => (
            <th key={field} title={label}>{shortLabel || label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.chain_id}>
            <td className="font-semibold">{row.chain}</td>
            <td>{row.client || "—"}</td>
            <td>{row.model || "—"}</td>
            <td>{row.category || "—"}</td>
            {metrics.map(([field]) => (
              <td key={field} className={isGapAlert(field, row[field]) ? "font-bold text-red-700" : ""}>
                {displayMetric(field, row[field])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-blue-50 font-bold">
        <tr>
          <td>Total</td>
          <td colSpan="3" />
          {metrics.map(([field]) => <td key={field} className={isGapAlert(field, totals[field]) ? "text-red-700" : ""}>{displayMetric(field, totals[field])}</td>)}
        </tr>
      </tfoot>
    </table>
  </div>
);

const useDailyRecap = (date) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!date) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    api.get("/daily-recap", { params: { date } })
      .then((response) => active && setRows(response.data?.rows || []))
      .catch((requestError) => {
        if (!active) return;
        setRows([]);
        setError(requestError.response?.data?.message || "Impossible de charger le récapitulatif.");
      })
      .finally(() => active && setLoading(false));

    return () => { active = false; };
  }, [date]);

  return { rows, loading, error };
};

const useWipRecap = (date, endpoint = "/wip-recap") => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!date) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    api.get(endpoint, { params: { date } })
      .then((response) => active && setRows(response.data?.rows || []))
      .catch((requestError) => {
        if (!active) return;
        setRows([]);
        setError(requestError.response?.data?.message || "Impossible de charger le suivi des encours.");
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [date, endpoint]);

  return { rows, loading, error };
};

const DailyRecapTables = () => {
  const [workforceDate, setWorkforceDate] = useState(today);
  const [productionDate, setProductionDate] = useState(today);
  const [wipDate, setWipDate] = useState(today);
  const [chainWipDate, setChainWipDate] = useState(today);
  const workforceRecap = useDailyRecap(workforceDate);
  const productionRecap = useDailyRecap(productionDate);
  const wipRecap = useWipRecap(wipDate);
  const chainWipRecap = useWipRecap(chainWipDate, "/chain-wip-recap");

  const workforceRows = useMemo(() => workforceRecap.rows.filter((row) =>
    (Number(row.real_direct) || 0) > 0
  ), [workforceRecap.rows]);
  const productionRows = useMemo(() => productionRecap.rows.filter((row) =>
    (Number(row.real_production) || 0) > 0 || (Number(row.planned_production) || 0) > 0
  ), [productionRecap.rows]);
  const workforceTotals = useMemo(() => calculateTotals(workforceRows), [workforceRows]);
  const productionTotals = useMemo(() => calculateTotals(productionRows), [productionRows]);
  const wipTotals = useMemo(() => {
    const entry = sum(wipRecap.rows, "entry");
    const output = sum(wipRecap.rows, "output");
    return {
      entry,
      work_in_progress: sum(wipRecap.rows, "work_in_progress"),
      output,
      progress_rate: entry > 0 ? (sum(wipRecap.rows, "work_in_progress") / entry) * 100 : 0,
    };
  }, [wipRecap.rows]);
  const chainWipTotals = useMemo(() => {
    const entry = sum(chainWipRecap.rows, "entry");
    const workInProgress = sum(chainWipRecap.rows, "work_in_progress");
    return {
      entry,
      work_in_progress: workInProgress,
      output: sum(chainWipRecap.rows, "output"),
      progress_rate: entry > 0 ? (workInProgress / entry) * 100 : 0,
    };
  }, [chainWipRecap.rows]);

  const metadataCells = (row) => (
    <>
      <td className={`${textCell} sticky left-0 z-10 bg-white font-semibold whitespace-nowrap`}>{row.chain}</td>
      <td className={textCell}>{row.client || "—"}</td>
      <td className={textCell}>{row.model || "—"}</td>
      <td className={textCell}>{row.category || "—"}</td>
    </>
  );

  const totalMetadata = (
    <>
      <td className={`${textCell} sticky left-0 z-10 bg-blue-50 font-bold`}>Total</td>
      <td className="border border-slate-300 bg-blue-50" />
      <td className="border border-slate-300 bg-blue-50" />
      <td className="border border-slate-300 bg-blue-50" />
    </>
  );

  const workforceMetrics = [
    ["real_direct", "Effectif réel direct", "Réel dir."],
    ["planned_direct", "Effectif prévisionnel direct", "Prév. dir."],
    ["direct_gap", "Écart direct", "Écart dir."],
    ["real_indirect", "Effectif réel indirect", "Réel ind."],
    ["planned_indirect", "Effectif prévisionnel indirect", "Prév. ind."],
    ["indirect_gap", "Écart indirect", "Écart ind."],
    ["real_worked_hours", "Heures travaillées réelles", "H. réelles"],
    ["planned_worked_hours", "Heures travaillées prévisionnelles", "H. prév."],
  ];
  const productionMetrics = [
    ["real_production", "Production réelle du jour", "Réel /J"],
    ["planned_production", "Production planning du jour", "Plan /J"],
    ["daily_production_gap", "Écart production / jour", "Écart /J"],
    ["cumulative_real_production", "Cumul production réelle", "Cumul réel"],
    ["cumulative_planned_production", "Cumul production planning", "Cumul plan"],
    ["cumulative_production_gap", "Écart production / cumul", "Écart cumul"],
    ["real_worked_hours", "Heures travaillées réelles", "H. réelles"],
    ["planned_worked_hours", "Heures travaillées planning", "H. plan"],
  ];

  return (
    <section className="flex min-w-0 flex-col gap-10 overflow-hidden" data-testid="developer-daily-recaps">
      <div className="order-2 min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-bold text-slate-900">Récap Suivi de l'Effectif Réel vs Prévisionnel</h3>
          <DateControl date={workforceDate} onChange={setWorkforceDate} />
        </div>
        {workforceRecap.error && <p className="mb-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{workforceRecap.error}</p>}
        {workforceRecap.loading && <p className="mb-3 rounded bg-slate-50 p-5 text-center text-sm text-slate-600">Chargement...</p>}
        <MobileRecap rows={workforceRows} metrics={workforceMetrics} totals={workforceTotals} isGapAlert={isWorkforceGapAlert} />
        <div className="hidden md:block">
        <StickyTableContainer>
          <table className="dashboard-sticky-table w-full min-w-[1450px] border-collapse bg-white text-sm">
            <thead>
              <tr>
                {["Chaîne", "Client", "Modèle", "Catégorie", "Effectif Réel direct", "Effectif prévisionnel direct", "Écart/effectif direct", "Effectif Réel indirect", "Effectif prévisionnel indirect", "Écart/effectif indirect", "Nombre d'heures travaillées Réel", "Nombre d'heures travaillées Prévisionnel"].map((label, index) => (
                  <th key={label} className={`${headerCell} ${index === 0 ? "sticky left-0 z-20" : ""}`}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {workforceRows.map((row) => (
                <tr key={row.chain_id}>
                  {metadataCells(row)}
                  {["real_direct", "planned_direct", "direct_gap", "real_indirect", "planned_indirect", "indirect_gap", "real_worked_hours", "planned_worked_hours"].map((field) => (
                    <td key={field} className={`${numericCell} ${isWorkforceGapAlert(field, row[field]) ? "font-bold text-red-700" : ""}`}>{displayMetric(field, row[field])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot><tr>{totalMetadata}{["real_direct", "planned_direct", "direct_gap", "real_indirect", "planned_indirect", "indirect_gap", "real_worked_hours", "planned_worked_hours"].map((field) => <td key={field} className={`${numericCell} bg-blue-50 font-bold ${isWorkforceGapAlert(field, workforceTotals[field]) ? "text-red-700" : ""}`}>{displayMetric(field, workforceTotals[field])}</td>)}</tr></tfoot>
          </table>
        </StickyTableContainer>
        </div>
      </div>

      <div className="order-1 min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-bold text-slate-900">Récap suivi de production Réel</h3>
          <DateControl date={productionDate} onChange={setProductionDate} />
        </div>
        {productionRecap.error && <p className="mb-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{productionRecap.error}</p>}
        {productionRecap.loading && <p className="mb-3 rounded bg-slate-50 p-5 text-center text-sm text-slate-600">Chargement...</p>}
        <MobileRecap rows={productionRows} metrics={productionMetrics} totals={productionTotals} isGapAlert={isProductionGapAlert} />
        <div className="hidden md:block">
        <StickyTableContainer>
          <table className="dashboard-sticky-table w-full min-w-[1450px] border-collapse bg-white text-sm">
            <thead>
              <tr>
                {["Chaîne", "Client", "Modèle", "Catégorie", "Production réelle du jour", "Production planning du jour", "Écart production /J", "Cumul production réelle", "Cumul production planning", "Écart production /cumul", "Nombre d'heures travaillées Réel", "Nombre d'heures travaillées planning"].map((label, index) => (
                  <th key={label} className={`${headerCell} ${index === 0 ? "sticky left-0 z-20" : ""}`}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {productionRows.map((row) => (
                <tr key={row.chain_id}>
                  {metadataCells(row)}
                  {["real_production", "planned_production", "daily_production_gap", "cumulative_real_production", "cumulative_planned_production", "cumulative_production_gap", "real_worked_hours", "planned_worked_hours"].map((field) => (
                    <td key={field} className={`${numericCell} ${isProductionGapAlert(field, row[field]) ? "font-bold text-red-700" : ""}`}>{displayMetric(field, row[field])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot><tr>{totalMetadata}{["real_production", "planned_production", "daily_production_gap", "cumulative_real_production", "cumulative_planned_production", "cumulative_production_gap", "real_worked_hours", "planned_worked_hours"].map((field) => <td key={field} className={`${numericCell} bg-blue-50 font-bold`}>{displayMetric(field, productionTotals[field])}</td>)}</tr></tfoot>
          </table>
        </StickyTableContainer>
        </div>
      </div>

      <div className="order-4 min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-bold text-slate-900">Récap Suivi des en cours / autres</h3>
          <DateControl date={wipDate} onChange={setWipDate} />
        </div>
        {wipRecap.error && <p className="mb-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{wipRecap.error}</p>}
        {wipRecap.loading && <p className="mb-3 rounded bg-slate-50 p-5 text-center text-sm text-slate-600">Chargement...</p>}

        <div className="w-full max-w-full overflow-hidden rounded border border-slate-300 md:hidden">
          <table className="mobile-production-table w-full table-fixed border-collapse bg-white text-[6px] leading-[8px] text-slate-800">
            <colgroup>
              <col className="w-[18%]" /><col className="w-[14%]" /><col className="w-[13%]" /><col className="w-[13%]" />
              <col className="w-[10%]" /><col className="w-[10%]" /><col className="w-[12%]" /><col className="w-[10%]" />
            </colgroup>
            <thead className="bg-slate-100 font-semibold text-slate-700">
              <tr><th>Stade</th><th>Modèle</th><th>Client</th><th>Cat.</th><th>Entrée</th><th>Encours</th><th>Cumul réel</th><th>Taux</th></tr>
            </thead>
            <tbody>
              {wipRecap.rows.map((row, index) => (
                <tr key={`${row.stage}-${row.model_id}`} className={index === 0 || wipRecap.rows[index - 1]?.model_id !== row.model_id ? "border-t-2 border-t-blue-300" : ""}>
                  <td className="font-semibold">{row.stage}</td><td>{row.model || "—"}</td><td>{row.client || "—"}</td><td>{row.category || "—"}</td>
                  <td>{displayNumber(row.entry)}</td><td>{displayNumber(row.work_in_progress)}</td><td>{displayNumber(row.output)}</td><td>{displayNumber(row.progress_rate)}%</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-blue-50 font-bold"><tr><td>Total</td><td colSpan="3" /><td>{displayNumber(wipTotals.entry)}</td><td>{displayNumber(wipTotals.work_in_progress)}</td><td>{displayNumber(wipTotals.output)}</td><td>{displayNumber(wipTotals.progress_rate)}%</td></tr></tfoot>
          </table>
        </div>

        <div className="hidden md:block">
          <StickyTableContainer>
            <table className="dashboard-sticky-table w-full min-w-[1050px] border-collapse bg-white text-sm">
              <thead><tr>{["Stade de production", "Modèle", "Client", "Catégorie", "Entrée", "Encours", "Cumul production réelle", "Taux en cours"].map((label, index) => <th key={label} className={`${headerCell} ${index === 0 ? "sticky left-0 z-20" : ""}`}>{label}</th>)}</tr></thead>
              <tbody>{wipRecap.rows.map((row, index) => <tr key={`${row.stage}-${row.model_id}`} className={index === 0 || wipRecap.rows[index - 1]?.model_id !== row.model_id ? "border-t-2 border-t-blue-300" : ""}>
                <td className={`${textCell} sticky left-0 z-10 bg-white font-semibold`}>{row.stage}</td><td className={textCell}>{row.model || "—"}</td><td className={textCell}>{row.client || "—"}</td><td className={textCell}>{row.category || "—"}</td>
                <td className={numericCell}>{displayNumber(row.entry)}</td><td className={numericCell}>{displayNumber(row.work_in_progress)}</td><td className={numericCell}>{displayNumber(row.output)}</td><td className={numericCell}>{displayNumber(row.progress_rate)}%</td>
              </tr>)}</tbody>
              <tfoot className="bg-blue-50 font-bold"><tr><td className={`${textCell} sticky left-0 z-10 bg-blue-50 font-bold`}>Total</td><td colSpan="3" className="border border-slate-300" /><td className={numericCell}>{displayNumber(wipTotals.entry)}</td><td className={numericCell}>{displayNumber(wipTotals.work_in_progress)}</td><td className={numericCell}>{displayNumber(wipTotals.output)}</td><td className={numericCell}>{displayNumber(wipTotals.progress_rate)}%</td></tr></tfoot>
            </table>
          </StickyTableContainer>
        </div>
      </div>

      <div className="order-3 min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-bold text-slate-900">Récap Suivi des en cours / chaînes</h3>
          <DateControl date={chainWipDate} onChange={setChainWipDate} />
        </div>
        {chainWipRecap.error && <p className="mb-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{chainWipRecap.error}</p>}
        {chainWipRecap.loading && <p className="mb-3 rounded bg-slate-50 p-5 text-center text-sm text-slate-600">Chargement...</p>}

        <div className="w-full max-w-full overflow-hidden rounded border border-slate-300 md:hidden">
          <table className="mobile-production-table w-full table-fixed border-collapse bg-white text-[6px] leading-[8px] text-slate-800">
            <colgroup><col className="w-[14%]" /><col className="w-[13%]" /><col className="w-[14%]" /><col className="w-[15%]" /><col className="w-[10%]" /><col className="w-[10%]" /><col className="w-[14%]" /><col className="w-[10%]" /></colgroup>
            <thead className="bg-slate-100 font-semibold text-slate-700"><tr><th>Chaîne</th><th>Client</th><th>Modèle</th><th>Cat.</th><th>Entrée</th><th>Encours</th><th>Cumul réel</th><th>Taux</th></tr></thead>
            <tbody>{chainWipRecap.rows.map((row) => <tr key={row.chain_id}>
              <td className="font-semibold">{row.chain}</td><td>{row.client || "—"}</td><td>{row.model || "—"}</td><td>{row.category || "—"}</td><td>{displayNumber(row.entry)}</td><td>{displayNumber(row.work_in_progress)}</td><td>{displayNumber(row.output)}</td><td>{displayNumber(row.progress_rate)}%</td>
            </tr>)}</tbody>
            <tfoot className="bg-blue-50 font-bold"><tr><td>Total</td><td colSpan="3" /><td>{displayNumber(chainWipTotals.entry)}</td><td>{displayNumber(chainWipTotals.work_in_progress)}</td><td>{displayNumber(chainWipTotals.output)}</td><td>{displayNumber(chainWipTotals.progress_rate)}%</td></tr></tfoot>
          </table>
        </div>

        <div className="hidden md:block">
          <StickyTableContainer>
            <table className="dashboard-sticky-table w-full min-w-[1050px] border-collapse bg-white text-sm">
              <thead><tr>{["Chaîne", "Client", "Modèle", "Catégorie", "Entrée", "Encours", "Cumul production réelle", "Taux en cours"].map((label, index) => <th key={label} className={`${headerCell} ${index === 0 ? "sticky left-0 z-20" : ""}`}>{label}</th>)}</tr></thead>
              <tbody>{chainWipRecap.rows.map((row) => <tr key={row.chain_id}>
                <td className={`${textCell} sticky left-0 z-10 bg-white font-semibold`}>{row.chain}</td><td className={textCell}>{row.client || "—"}</td><td className={textCell}>{row.model || "—"}</td><td className={textCell}>{row.category || "—"}</td><td className={numericCell}>{displayNumber(row.entry)}</td><td className={numericCell}>{displayNumber(row.work_in_progress)}</td><td className={numericCell}>{displayNumber(row.output)}</td><td className={numericCell}>{displayNumber(row.progress_rate)}%</td>
              </tr>)}</tbody>
              <tfoot className="bg-blue-50 font-bold"><tr><td className={`${textCell} sticky left-0 z-10 bg-blue-50 font-bold`}>Total</td><td colSpan="3" className="border border-slate-300" /><td className={numericCell}>{displayNumber(chainWipTotals.entry)}</td><td className={numericCell}>{displayNumber(chainWipTotals.work_in_progress)}</td><td className={numericCell}>{displayNumber(chainWipTotals.output)}</td><td className={numericCell}>{displayNumber(chainWipTotals.progress_rate)}%</td></tr></tfoot>
            </table>
          </StickyTableContainer>
        </div>
      </div>
    </section>
  );
};

export default DailyRecapTables;
